"use client";

import { useCart } from "@/cart";
import { ReactNode, useEffect, useRef } from "react";
import {
  ADD_TO_CART_SELECTOR,
  PRICE_MARKUP,
  PRICE_SELECTOR,
} from "./constants";
import { usePricelist } from "@/pricelist/PricelistContext";
import { NormalizedProductVariant } from "@cappelendamm/catalogue/crystallize/product-translator.js";
import { getPriceOffer } from "@/products/pricing";

export function PromoShell({
  children,
  product,
}: {
  children: ReactNode;
  product?: NormalizedProductVariant;
}) {
  const { addItem } = useCart();
  const { applicablePricelists } = usePricelist();
  const ref = useRef<HTMLDivElement>(null);
  const safePricelists = Array.isArray(product?.priceLists)
    ? product.priceLists
    : [];

  const price = getPriceOffer({
    originalItemPrice: product?.price,
    subjectToVAT: product?.subjectToVAT,
    priceLists: safePricelists.filter((pricelist) =>
      applicablePricelists?.some(
        (applicable) => applicable.node?.identifier === pricelist.identifier,
      ),
    ),
  });

  useEffect(() => {
    if (product?.price && price.hasOffer) {
      ref.current?.querySelectorAll(PRICE_SELECTOR).forEach((el) => {
        el.textContent = el.textContent
          .replaceAll(String(product.price), String(price.current))
          .replaceAll(PRICE_MARKUP, String(price.current));
      });
    }
  }, [product?.price, price.current, price.hasOffer]);

  useEffect(() => {
    const addToCartListener = (event: MouseEvent) => {
      const { sku } = (event.currentTarget as HTMLButtonElement).dataset;
      if (sku) addItem({ sku });
    };
    const buttons: NodeListOf<HTMLButtonElement> | undefined =
      ref.current?.querySelectorAll(ADD_TO_CART_SELECTOR) ?? undefined;

    buttons?.forEach((el: HTMLButtonElement) =>
      el.addEventListener("click", addToCartListener),
    );

    return () => {
      buttons?.forEach((el: HTMLButtonElement) =>
        el.removeEventListener("click", addToCartListener),
      );
    };
  }, [addItem]);

  return <div ref={ref}>{children}</div>;
}
